/* v3.4 — Editorial bands
   Showcase: SinceYouLastLookedDigest as the editorial centerpiece — Jacinta
   hand-picked the 3-5 things that changed since you last visited.
   Practical: dark band dividers create chapter focus; mini calendar + funnel
   in the side rail; approvals and roles get clean editorial table treatments. */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useDashboardData, buildTaskRoute } from '../useDashboardData';
import type { JobPosting } from '@/modules/job/services/job.api';
import type { TaskItem } from '@/modules/shared/types/yc-demo';
import type { SyncedCalendarEvent } from '@/modules/calendar/types/calendar.types';
import { VariantSwitcher } from './VariantSwitcher';
import { SinceYouLastLookedDigest } from '../widgets/SinceYouLastLookedDigest';
import { MiniCalendar } from '../widgets/MiniCalendar';
import { FunnelChart } from '../widgets/FunnelChart';
import styles from './home-v3-4.module.css';

function greetingFor(d: Date) {
    const h = d.getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
}

function formatTime(iso: string) {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function relativeAgo(ms: number) {
    const m = Math.max(1, Math.round(ms / 60_000));
    if (m < 60) return `${m}m ago`;
    return `${Math.round(m / 60)}h ago`;
}

function hash(s: string) {
    return s.split('').reduce((a, c) => (a * 31 + c.charCodeAt(0)) & 0xffff, 7);
}

function buildBriefing(userName: string, jobs: JobPosting[], tasks: TaskItem[], events: SyncedCalendarEvent[]) {
    const greeting = `${greetingFor(new Date())}, ${userName.split(' ')[0]}.`;
    const primary = jobs[0];
    const secondary = jobs[1];
    const high = tasks.filter((t) => t.priority === 'high');
    const next = events[0];
    const sourced = primary ? 31 + (hash(primary._id) % 32) : 0;
    const screened = Math.max(8, Math.round(sourced * 0.6));
    const passed = Math.max(high.length, Math.round(screened * 0.25));

    if (jobs.length === 0 && tasks.length === 0) return { greeting, paragraph: 'Quiet morning. Nothing pending.' };
    const lines: string[] = [];
    if (primary) lines.push(`Sourced ${sourced} candidates for **${primary.title}** overnight, screened ${screened}, ${passed} passed my bar.${high.length > 0 ? ` ${high.length} need your judgment.` : ''}`);
    if (secondary) lines.push(`Pre-drafted outreach for ${8 + (hash(secondary._id) % 9)} warm referrals on **${secondary.title}**.`);
    if (next) lines.push(`${next.attendees?.[0]?.name?.split(' ')[0] || 'Someone'} confirmed at ${formatTime(next.startDateTime)}.`);
    return { greeting, paragraph: lines.join(' ') };
}

function renderBold(text: string) {
    return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
        p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>,
    );
}

function initials(name: string) {
    const p = name.trim().split(/\s+/);
    if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
    return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

function Band({ title, meta, withIndicator = false }: { title: string; meta?: string; withIndicator?: boolean }) {
    return (
        <div className={styles.band}>
            <div className={styles.bandInner}>
                <h2 className={styles.bandTitle}>
                    {withIndicator ? (
                        <span className={styles.bandWithIndicator}>
                            <span className={styles.bandDot} aria-hidden /> {title}
                        </span>
                    ) : (
                        title
                    )}
                </h2>
                {meta && <span className={styles.bandMeta}>{meta}</span>}
            </div>
        </div>
    );
}

export function HomeV34() {
    const { state, userName } = useDashboardData();
    const navigate = useNavigate();
    const briefing = useMemo(() => state.phase === 'ready' ? buildBriefing(userName, state.data.jobs, state.data.tasks, state.data.events) : null, [state, userName]);

    if (state.phase === 'loading') {
        return (
            <main className={styles.page}>
                <div className={styles.container}>
                    <VariantSwitcher current="v3.4" className={styles.variantBar} activeClass={styles.active} />
                    <div className={styles.skeleton} />
                </div>
            </main>
        );
    }
    if (state.phase === 'error') {
        return <main className={styles.page}><div className={styles.container}><div className={styles.errorBox}>{state.message}</div></div></main>;
    }

    const { jobs, tasks, events, lastActionAgoMs } = state.data;
    const high = tasks.filter((t) => t.priority === 'high');
    const primary = jobs[0];
    const secondary = jobs[1];
    const next = events[0];
    const today = new Date();
    const dateLabel = today.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
    const dayLabel = today.toLocaleDateString(undefined, { weekday: 'long' });

    const chips: Array<{ label: string; href: string; primary?: boolean }> = [];
    if (high.length > 0) chips.push({ label: `${high.length} need your judgment`, href: '/v2/jobs', primary: true });
    if (next) chips.push({ label: `${next.attendees?.[0]?.name?.split(' ')[0] || 'Next'} at ${formatTime(next.startDateTime)}`, href: '/v2/calendar' });
    if (secondary) chips.push({ label: 'Approve outreach', href: `/v2/jobs/${secondary._id}/plan` });
    chips.push({ label: 'Lantern brain', href: '/brain' });

    return (
        <main className={styles.page}>
            <div className={styles.container}>
                <VariantSwitcher current="v3.4" className={styles.variantBar} activeClass={styles.active} />
            </div>

            {/* CHAPTER 1 — BRIEFING */}
            <Band title="Morning briefing" meta={dateLabel} withIndicator />
            <div className={styles.container}>
                <section className={styles.chapter}>
                    <div className={styles.hero}>
                        <div className={styles.heroAvatar}>
                            <img src="/avatars/jacinta.svg" alt="" />
                        </div>
                        <div className={styles.heroBody}>
                            <div className={styles.heroIdent}>
                                <span className={styles.heroName}>Jacinta</span>
                                <span className={styles.heroStatus}>
                                    <span className={styles.heroDot} aria-hidden />
                                    last action {relativeAgo(lastActionAgoMs)}
                                </span>
                            </div>
                            <h1 className={styles.heroGreeting}>{briefing?.greeting}</h1>
                            <p className={styles.heroParagraph}>{briefing && renderBold(briefing.paragraph)}</p>
                            <div className={styles.chips}>
                                {chips.slice(0, 4).map((c, i) => (
                                    <button key={c.href + i} className={`${styles.chip} ${c.primary ? styles.chipPrimary : ''}`} onClick={() => navigate(c.href)}>
                                        {c.label}<ArrowRight size={11} strokeWidth={2.25} />
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className={styles.heroAside}>
                            <div className={styles.heroDate}>{dateLabel}</div>
                            <div>{dayLabel}</div>
                        </div>
                    </div>
                </section>
            </div>

            {/* CHAPTER 2 — DIGEST + SIDE RAIL */}
            <Band title="Since you last looked" meta="edited by Jacinta" />
            <div className={styles.container}>
                <section className={styles.chapter}>
                    <div className={styles.twoCol}>
                        <SinceYouLastLookedDigest jobs={jobs} tasks={tasks} events={events} surface="rgba(255,255,255,0.7)" rule="rgba(120, 90, 50, 0.08)" />
                        <div className={styles.sideRail}>
                            <MiniCalendar events={events} surface="rgba(255,255,255,0.7)" rule="rgba(120, 90, 50, 0.08)" />
                            <FunnelChart job={primary} surface="rgba(255,255,255,0.7)" rule="rgba(120, 90, 50, 0.08)" />
                        </div>
                    </div>
                </section>
            </div>

            {/* CHAPTER 3 — APPROVALS */}
            <Band title="Pending approvals" meta={`${tasks.length} waiting on you`} />
            <div className={styles.container}>
                <section className={styles.chapter}>
                    <div className={styles.row}>
                        {tasks.slice(0, 6).map((task) => {
                            const cls = task.priority === 'high' ? styles.priorityHigh : task.priority === 'low' ? styles.priorityLow : styles.priorityNormal;
                            return (
                                <button key={task._id} className={styles.approvalRow} onClick={() => navigate(buildTaskRoute(task))}>
                                    <div className={styles.approvalAvatar}>{initials(task.candidateName)}</div>
                                    <div className={styles.approvalIdent}>
                                        <div className={styles.approvalName}>{task.candidateName}</div>
                                        <div className={styles.approvalSub}>{task.jobTitle}</div>
                                    </div>
                                    <span className={`${styles.kindPill} ${task.kind === 'cv_review' ? styles.kindCv : styles.kindInterview}`}>
                                        {task.kind === 'cv_review' ? 'CV' : 'Int'}
                                    </span>
                                    <span className={`${styles.priorityDot} ${cls}`} aria-label={task.priority} />
                                </button>
                            );
                        })}
                        {tasks.length === 0 && <div className={styles.empty}>None pending.</div>}
                    </div>
                </section>
            </div>

            {/* CHAPTER 4 — ROLES */}
            <Band title="Active roles" meta={`${jobs.length} open`} />
            <div className={styles.container}>
                <section className={styles.chapter}>
                    <div className={styles.row}>
                        {jobs.slice(0, 4).map((job) => {
                            const seed = hash(job._id);
                            const sourced = 35 + (seed % 23);
                            return (
                                <button key={job._id} className={styles.roleRow} onClick={() => navigate(`/v2/jobs/${job._id}/plan`)}>
                                    <div>
                                        <div className={styles.roleTitle}>{job.title}</div>
                                        <div className={styles.roleMeta}>{job.department || job.location || 'Workspace'}</div>
                                    </div>
                                    <div>
                                        <div className={styles.roleNumeral}>{sourced}</div>
                                        <span className={styles.roleNumeralLabel}>sourced</span>
                                    </div>
                                    <ArrowRight className={styles.roleArrow} size={18} strokeWidth={1.5} />
                                </button>
                            );
                        })}
                    </div>
                </section>
            </div>
        </main>
    );
}
